import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { DataService } from '@/services/dataService';
import { queryKeys } from '@/hooks/queries/queryKeys';
import { supabase } from '@/lib/supabaseClient';
import { WorkOrder } from '@/types';

const ORDERS_PAGE_SIZE = 50;
const ACTIVE_ORDERS_PAGE_SIZE = 200;

export function useOrdersInfiniteQuery(enabled: boolean) {
    const activeQuery = useInfiniteQuery({
        // queryKey structure needs to distinguish the filter
        queryKey: [...queryKeys.orders(), { isArchived: false }],
        queryFn: ({ pageParam }) => DataService.getOrders(pageParam, ACTIVE_ORDERS_PAGE_SIZE, { isArchived: false }),
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
            if (lastPage.orders.length < ACTIVE_ORDERS_PAGE_SIZE) return undefined;
            return allPages.length + 1;
        },
        enabled,
    });

    const activeOrders = activeQuery.data?.pages.flatMap((p) => p.orders) ?? [];

    const loadMoreActiveOrders = async (): Promise<boolean> => {
        if (!activeQuery.hasNextPage) return false;
        await activeQuery.fetchNextPage();
        return (activeQuery.data?.pages.flatMap((p) => p.orders).length ?? 0) > activeOrders.length;
    };

    return {
        // Active orders only
        orders: activeOrders,
        activeOrders,
        archivedOrders: [], // Legacy compat
        isLoading: activeQuery.isLoading,
        isFetching: activeQuery.isFetching,
        loadMoreActiveOrders,
        loadMoreArchivedOrders: async () => false, // Legacy compat
        loadMoreOrders: loadMoreActiveOrders,
        hasNextPageActive: activeQuery.hasNextPage,
        hasNextPageArchived: false,
        refetchActive: activeQuery.refetch,
        refetchArchived: () => {},
        error: activeQuery.error
    };
}

export function useArchivedOrdersInfiniteQuery(
    searchQuery?: string,
    clientIds?: string[],
    dateRange?: { start?: string; end?: string } | null,
    enabled: boolean = true
) {
    const archivedQuery = useInfiniteQuery({
        queryKey: [...queryKeys.orders(), { isArchived: true, searchQuery, clientIds, dateRange }],
        queryFn: ({ pageParam }) => DataService.getOrders(pageParam, ORDERS_PAGE_SIZE, { 
            isArchived: true, 
            searchQuery, 
            clientIds, 
            dateRange 
        }),
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
            if (lastPage.orders.length < ORDERS_PAGE_SIZE) return undefined;
            return allPages.length + 1;
        },
        enabled,
    });

    const archivedOrders = archivedQuery.data?.pages.flatMap((p) => p.orders) ?? [];

    const loadMoreArchivedOrders = async (): Promise<boolean> => {
        if (!archivedQuery.hasNextPage) return false;
        await archivedQuery.fetchNextPage();
        return (archivedQuery.data?.pages.flatMap((p) => p.orders).length ?? 0) > archivedOrders.length;
    };

    return {
        archivedOrders,
        isLoading: archivedQuery.isLoading,
        isFetching: archivedQuery.isFetching,
        loadMoreArchivedOrders,
        hasNextPageArchived: archivedQuery.hasNextPage,
        refetchArchived: archivedQuery.refetch,
        error: archivedQuery.error
    };
}

/**
 * V_DIRECT: Carga un parte individual por ID cuando no se encuentra en la lista local.
 * Útil cuando un técnico accede a un parte completado/archivado desde el índice
 * y la nueva instancia del componente no lo tiene en effectiveOrders.
 */
export function useSingleOrderQuery(orderId: string | undefined, skip: boolean) {
    return useQuery<WorkOrder | null>({
        queryKey: [...queryKeys.orders(), 'single', orderId],
        queryFn: () => DataService.getOrderById(orderId!),
        enabled: Boolean(orderId) && !skip,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });
}

/**
 * V_CHAIN: Carga los partes de la misma cadena (mismo workSiteId+clientId o rootOrderId)
 * directamente desde Supabase, sin cargar todos los archivados en memoria.
 * staleTime alto para evitar re-fetches que causarían horas aleatorias.
 */
export function useChainOrdersQuery(workSiteId?: string | null, clientId?: string | null, rootOrderId?: string | null) {
    const enabled = Boolean((workSiteId || clientId) && !rootOrderId?.startsWith('temp-'));

    return useQuery<WorkOrder[]>({
        queryKey: [...queryKeys.orders(), 'chain', workSiteId, clientId, rootOrderId],
        queryFn: async () => {
            let query = supabase
                .from('work_orders')
                .select(`
                    *,
                    clients (name),
                    work_sites (name),
                    wo_labor_journal (*, wo_labor_companions (*))
                `);

            if (rootOrderId) {
                const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rootOrderId);
                let rootUuid: string | null = isUuid ? rootOrderId : null;
                if (!isUuid) {
                    const { data: parent } = await supabase
                        .from('work_orders')
                        .select('id')
                        .eq('visible_id', rootOrderId)
                        .maybeSingle();
                    rootUuid = parent?.id || null;
                }
                if (rootUuid) {
                    query = query.or(`root_order_id.eq.${rootUuid},id.eq.${rootUuid}`);
                } else {
                    query = query.eq('visible_id', rootOrderId);
                }
            } else if (workSiteId) {
                query = query.eq('work_site_id', workSiteId);
            } else if (clientId) {
                query = query.eq('client_id', clientId);
            }

            const { data, error } = await query.order('created_at', { ascending: true });
            if (error) throw error;

            return (data || []).map((row: any) => {
                const laborJournal = (row.wo_labor_journal || []).map((j: any) => ({
                    id: j.id,
                    date: j.date,
                    mainTechHours: j.main_hours,
                    technicianId: j.technician_id,
                    notes: j.notes,
                    companions: (j.wo_labor_companions || []).map((c: any) => ({ name: c.name, hours: c.hours })),
                    signature: j.signature,
                    isAbsent: j.is_absent,
                }));
                return {
                    id: row.visible_id,
                    _uuid: row.id,
                    clientId: row.client_id,
                    clientName: row.clients?.name || '',
                    workSiteId: row.work_site_id,
                    rootOrderId: row.root_order_id || undefined,
                    technicianId: row.technician_id,
                    status: row.status,
                    laborJournal,
                    createdAt: row.created_at,
                    title: row.work_sites?.name || row.title,
                    obra: row.work_sites?.name || row.title || '',
                } as WorkOrder;
            });
        },
        enabled,
        staleTime: 10 * 60 * 1000,  // 10 min — no re-fetch aleatorio
        gcTime: 15 * 60 * 1000,
        refetchOnWindowFocus: false, // Nunca re-fetch por foco
        refetchOnMount: false,       // No re-fetch innecesario al montar
    });
}
